import { chipLabel } from '../../lib/tagLabel'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styles from './LiveBoard.module.css'
import { useAuth } from '../../contexts/AuthContext'
import { apiJson } from '../../lib/api'
import { dateLabel, todayLocal } from '../timesheets/time'
import { useLayer } from '../../components/layers'
import { useLive } from '../../lib/live'
import {
  initials,
  strikeEmpty,
  strikeTotal,
  tallyGroups,
  type LeaderResult,
  type Strike,
  type StrikeResult,
  type StrikeWorker,
} from './model'

// The live dashboards (handoff §7l.7; CONTROL V4 rpLiveOn). One shell for
// both boards: over the library as a modal, or alone in its own tab as a
// wallboard (/live/:key, no rail) with a Full screen button. Big type, dark
// ground, pushed as it changes (every minute while the live stream is down),
// and the wall scrolls itself.
//
// Every figure is the server's: the Leaderboard from the wage basis at Daily
// scope, the Strike Board from the effective cycles. Nothing is computed or
// seeded here. There is no defect photography in the product, so a strike
// shows an honest "No photo" tile where V4 has its image slot.

export const REFRESH_MS = 60_000
const MAX_SHOTS = 6

export interface LiveDef {
  key: string
  name: string
  periods: { k: string; label: string }[]
}

interface Props {
  def: LiveDef
  wall: boolean
  onClose?: () => void
  initialPeriod?: string | null
  initialTeam?: string | null
}

type Data = LeaderResult | StrikeResult

export function LiveBoard({ def, wall, onClose, initialPeriod, initialTeam }: Props) {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [period, setPeriod] = useState(() => (def.periods.some((p) => p.k === initialPeriod) ? initialPeriod! : def.periods[0]?.k || 'today'))
  const [team, setTeam] = useState(initialTeam || '')
  const [data, setData] = useState<Data | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [shot, setShot] = useState<{ worker: string; s: Strike } | null>(null)
  const [toast, setToast] = useState('')
  const userRef = useRef(user)
  userRef.current = user
  const uid = user?.uid
  const seq = useRef(0)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const load = useCallback(async () => {
    const u = userRef.current
    if (!u) return
    const n = ++seq.current
    const p = new URLSearchParams({ period, today: todayLocal() })
    if (def.key === 'leader' && team) p.set('team', team)
    try {
      const r = await apiJson<Data>(u, `/api/live/${encodeURIComponent(def.key)}?${p}`)
      if (n !== seq.current) return
      setData(r)
      setError(null)
    } catch (e: any) {
      if (n !== seq.current) return
      // A failed refresh keeps the last figures on the wall and says so.
      setError(e?.message || 'Could not load this board')
    }
  }, [def.key, period, team, uid])

  // Every period ends today, so the board is pushed (h 4167): the Leaderboard
  // hears the wage basis being rebuilt, the Strike Board its cycles. Today
  // listens to today; a longer period to any date up to today. The minute's
  // poll is the fallback, run only while the live stream is down.
  useEffect(() => {
    load()
  }, [load])
  const today = todayLocal()
  const live = useLive(user, def.key === 'leader' ? ['wages'] : ['cycles'], period === 'today' ? (d) => d === today : (d) => d <= today, load)
  useEffect(() => {
    if (live === 'live') return
    const t = setInterval(load, REFRESH_MS)
    return () => clearInterval(t)
  }, [load, live])

  useEffect(() => {
    if (!toast) return
    const t = setTimeout(() => setToast(''), 3400)
    return () => clearTimeout(t)
  }, [toast])

  // Over the library the board is a layer (h 425-458): Escape and the scrim
  // close it. On the wall there is nothing to close it to, so it is not one.
  // The quality-issue viewer is a layer of its own over it (ShotViewer), so
  // Escape closes the viewer first and the board on a second press.
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const boardEl = useRef<HTMLDivElement>(null)
  useLayer(!!onClose, () => closeRef.current?.(), { el: boardEl, modal: !wall })

  useWallScroll(scrollRef, `${def.key}|${period}|${team}`)

  const popOut = () => {
    const p = new URLSearchParams({ period })
    if (def.key === 'leader' && team) p.set('team', team)
    // Not noopener: the new tab takes a copy of this tab's workspace choice.
    window.open(`/live/${encodeURIComponent(def.key)}?${p}`, '_blank')
  }
  const fullScreen = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen()
      else await document.documentElement.requestFullscreen()
    } catch {
      setToast('Full screen was refused by the browser')
    }
  }

  const periodButtons = (
    <div className={styles.periods} role="group" aria-label="Period">
      {def.periods.map((p) => (
        <button key={p.k} type="button" aria-pressed={period === p.k} className={period === p.k ? styles.periodOn : styles.period} onClick={() => setPeriod(p.k)}>
          {p.label}
        </button>
      ))}
    </div>
  )

  const isLeader = def.key === 'leader'
  const board = (
    <div ref={boardEl} className={`${styles.board} ${wall ? styles.wall : styles.modal}`} role={wall ? undefined : 'dialog'} aria-label={def.name} onClick={(e) => e.stopPropagation()}>
      <div className={styles.head}>
        <h1 className={styles.headTitle}>{def.name}</h1>
        <span className={styles.liveTag}>
          <span className={styles.liveDot} />
          LIVE
        </span>
        {wall ? (
          <button type="button" className={styles.headBtn} onClick={fullScreen}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M8 3H5a2 2 0 0 0-2 2v3" /><path d="M21 8V5a2 2 0 0 0-2-2h-3" /><path d="M3 16v3a2 2 0 0 0 2 2h3" /><path d="M16 21h3a2 2 0 0 0 2-2v-3" />
            </svg>
            Full screen
          </button>
        ) : (
          <>
            <button type="button" className={styles.headBtn} onClick={popOut}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
              </svg>
              Open for a wallboard
            </button>
            <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close" title="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </>
        )}
      </div>

      <div className={isLeader ? styles.navy : styles.red}>
        <div className={styles.top}>
          <div className={styles.bigTitle}>{def.name}</div>
          <div className={styles.controls}>
            {periodButtons}
            {isLeader && (
              <select aria-label="Team" className={styles.teamSelect} value={team} onChange={(e) => setTeam(e.target.value)}>
                <option value="">All teams</option>
                {((data as LeaderResult | null)?.teams || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          {isLeader ? (
            <div className={styles.sub}>Ranked on credited kg per hour · {(data as LeaderResult | null)?.gate_hours ?? 2}-hour minimum for a podium place</div>
          ) : (
            data && <div className={styles.subStrong}>{strikeTotal(data as StrikeResult)}</div>
          )}
          {error && <div className={styles.error}>{data ? `Not refreshed: ${error}. Showing the last figures.` : error}</div>}
          {data?.notes.map((n) => (
            <div key={n} className={styles.note}>
              {n}
            </div>
          ))}
        </div>

        <div ref={scrollRef} className={styles.scroll}>
          {!data && !error && <div className={styles.empty}>Loading…</div>}
          {data && isLeader && <LeaderRows data={data as LeaderResult} onOpen={(name) => openWorker(navigate, name, data.window)} />}
          {data && !isLeader && <StrikeRows data={data as StrikeResult} onShot={(worker, s) => setShot({ worker, s })} />}
        </div>
      </div>

      {shot && (
        <ShotViewer
          worker={shot.worker}
          s={shot.s}
          onClose={() => setShot(null)}
          onGo={() => navigate(`/product-activity?${new URLSearchParams({ cycle: shot.s.cycle_id, from: shot.s.work_date, to: shot.s.work_date, tab: 'evidence' })}`)}
        />
      )}
      {toast && <div className={styles.toast} role="status">{toast}</div>}
    </div>
  )

  if (wall) return board
  return (
    <div className={styles.backdrop} onClick={onClose}>
      {board}
    </div>
  )
}

function openWorker(navigate: ReturnType<typeof useNavigate>, name: string, w: { from: string | null; to: string }) {
  const p = new URLSearchParams({ worker: name })
  if (w.from) p.set('from', w.from)
  p.set('to', w.to)
  navigate(`/product-activity?${p}`)
}

const MEDALS = ['#e8b04b', '#c9c9cf', '#c07a3e']
const kgText = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 })

function LeaderRows({ data, onOpen }: { data: LeaderResult; onOpen: (name: string) => void }) {
  if (!data.rows.length) return <div className={styles.empty}>Nobody has recorded picking work in this period.</div>
  return (
    <>
      {data.rows.map((w, i) => (
        <div
          key={w.human_id}
          role="button"
          tabIndex={0}
          aria-label={`#${w.rank} ${w.name}`}
          className={`${styles.lbRow} ${i === 0 ? styles.lbGold : i === 2 ? styles.lbBronze : ''}`}
          onClick={() => onOpen(w.name)}
          onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onOpen(w.name))}
        >
          <span className={styles.rank}>#{w.rank}</span>
          {i === 0 && (
            <svg viewBox="0 0 24 24" fill="currentColor" className={styles.medal} style={{ color: MEDALS[0] }} aria-label="Gold trophy" role="img">
              <path d="M18 3h3.2c.4 0 .8.4.8.8V6c0 2.6-1.9 4.7-4.4 5.2a6 6 0 0 1-3.1 2.6V17h2.8c.6 0 1 .4 1 1v.6h-12V18c0-.6.4-1 1-1H10v-3.2a6 6 0 0 1-3.1-2.6C4.4 10.7 2.5 8.6 2.5 6V3.8c0-.4.4-.8.8-.8H6V2h12ZM6 5.2H4.6V6c0 1.3.8 2.4 1.9 2.9A8 8 0 0 1 6 7.4Zm12 0v2.2c0 .5 0 1-.2 1.5 1.1-.5 1.9-1.6 1.9-2.9v-.8ZM5.5 20.2h13c.6 0 1 .4 1 1v.8h-15v-.8c0-.6.4-1 1-1Z" />
            </svg>
          )}
          {(i === 1 || i === 2) && (
            <svg viewBox="0 0 24 24" className={styles.medal} style={{ color: MEDALS[i] }} aria-label={i === 1 ? 'Silver medal' : 'Bronze medal'} role="img">
              <path d="M7.2 2h3.3L7.8 8.4 4.5 7.1Z" fill="currentColor" opacity=".72" />
              <path d="M16.8 2h-3.3l2.7 6.4 3.3-1.3Z" fill="currentColor" opacity=".72" />
              <circle cx="12" cy="15.4" r="6.4" fill="currentColor" />
              <circle cx="12" cy="15.4" r="4.3" fill="none" stroke="#152449" strokeWidth="1" opacity=".38" />
            </svg>
          )}
          <span className={styles.lbAvatar} aria-hidden>
            {initials(w.name)}
          </span>
          <div className={styles.who}>
            <span className={styles.lbName}>{w.name}</span>
            <span className={styles.lbTeams}>{w.teams.join(', ')}</span>
            {w.gated && <span className={styles.gated}>Fast, but under the {data.gate_hours}-hour minimum for a podium place</span>}
          </div>
          <div className={styles.stats}>
            <Stat k="kg / hour" v={w.rate.toFixed(1)} tone="rate" big />
            <Stat k="Items" v={w.items.toLocaleString()} tone="items" />
            <Stat k="Credited kg" v={kgText(w.kg)} tone="kg" />
            <Stat k="Hours" v={w.hours.toFixed(1)} tone="hours" />
          </div>
        </div>
      ))}
    </>
  )
}

function Stat({ k, v, tone, big = false }: { k: string; v: string; tone: 'rate' | 'items' | 'kg' | 'hours'; big?: boolean }) {
  return (
    <div className={styles.stat}>
      <span className={styles.statK}>{k}</span>
      <span className={`${styles.statV} ${styles[tone]} ${big ? styles.statBig : ''}`}>{v}</span>
    </div>
  )
}

function StrikeRows({ data, onShot }: { data: StrikeResult; onShot: (worker: string, s: Strike) => void }) {
  if (!data.workers.length) return <div className={styles.emptyRed}>{strikeEmpty(data.period)}</div>
  return (
    <>
      {data.workers.map((w: StrikeWorker, i) => (
        <div key={w.human_id} className={`${styles.stRow} ${i === 0 ? styles.stTop : ''}`}>
          <span className={styles.stRank}>#{i + 1}</span>
          <span className={styles.stAvatar} aria-hidden>
            {initials(w.name)}
          </span>
          <div className={styles.who}>
            <span className={styles.stName}>{w.name}</span>
            <span className={styles.stIssues}>{w.issues.join(' · ')}</span>
          </div>
          <div className={styles.tally}>
            <span className={styles.tallyK}>Strikes</span>
            <div className={styles.tallyMarks} role="img" aria-label={`${w.n} ${w.n === 1 ? 'strike' : 'strikes'}`}>
              {tallyGroups(w.n).map((g, j) => (
                <span key={j} className={styles.gate} data-five={g.five || undefined}>
                  {Array.from({ length: g.marks }, (_, m) => (
                    <span key={m} className={styles.mark} />
                  ))}
                  {g.five && <span className={styles.slash} />}
                </span>
              ))}
            </div>
          </div>
          <div className={styles.shots}>
            {w.strikes.slice(0, MAX_SHOTS).map((s) => (
              <button key={s.cycle_id} type="button" className={styles.shot} onClick={() => onShot(w.name, s)} aria-label={`${s.quality}, ${chipLabel(s.item_code)}`} title={`${s.quality} · ${chipLabel(s.item_code)}`}>
                {s.photo ? <img src={s.photo} alt="" /> : <span className={styles.noPhoto}>No photo</span>}
              </button>
            ))}
            {w.strikes.length > MAX_SHOTS && <span className={styles.more}>+{w.strikes.length - MAX_SHOTS}</span>}
          </div>
        </div>
      ))}
    </>
  )
}

const kgFact = (n: number | null) => (n === null || n === undefined ? '—' : `${n.toFixed(1)} kg`)

function ShotViewer({ worker, s, onClose, onGo }: { worker: string; s: Strike; onClose: () => void; onGo: () => void }) {
  const facts = [
    { k: 'Item', v: chipLabel(s.item_code) },
    { k: 'Picked', v: dateLabel(s.work_date) },
    { k: 'Location', v: s.location || '—' },
    { k: 'Gross weight', v: kgFact(s.gross_kg) },
    { k: 'Credited', v: kgFact(s.credited_kg) },
    // A pending deduction's factor is the scan's; the credited weight above
    // does not include it until the day closes.
    { k: s.pending ? 'Factor, not yet applied' : 'Factor applied', v: s.factor === null ? '—' : `${Math.round(s.factor * 100)}%` },
  ]
  // A layer (h 425-458), on the wall as well as over the library.
  const cardEl = useRef<HTMLDivElement>(null)
  useLayer(true, onClose, { el: cardEl, modal: true })
  return (
    <div className={styles.shotBackdrop} onClick={onClose}>
      <div ref={cardEl} className={styles.shotCard} role="dialog" aria-label="Quality issue" onClick={(e) => e.stopPropagation()}>
        <div className={styles.shotPhoto}>
          {s.photo ? (
            <img src={s.photo} alt={`${s.quality} on ${chipLabel(s.item_code)}`} />
          ) : (
            <div className={styles.shotNone}>No photo. Control does not store defect photos yet, so there is none to show for this check.</div>
          )}
        </div>
        <div className={styles.shotSide}>
          <div className={styles.shotHead}>
            <div className={styles.shotHeadText}>
              <div className={styles.shotKicker}>Quality issue</div>
              <div className={styles.shotQc}>{s.quality}</div>
              <div className={styles.shotWho}>Picked by {worker}</div>
            </div>
            <button type="button" className={styles.shotClose} onClick={onClose} aria-label="Close" title="Close">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden>
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          {facts.map((f) => (
            <div key={f.k} className={styles.fact}>
              <span className={styles.factK}>{f.k}</span>
              <span className={styles.factV}>{f.v}</span>
            </div>
          ))}
          {s.provisional && <div className={styles.provisional}>Provisional: this deduction becomes production-effective when the day closes.</div>}
          <button type="button" className={styles.shotGo} onClick={onGo}>
            Open the cycle in Product activity
          </button>
        </div>
      </div>
    </div>
  )
}

// §7l.7: five seconds at the top, a slow drift to the bottom, a two-second
// pause, then back to the top at double speed, on a loop. Suppressed under
// prefers-reduced-motion, and idle when everything fits.
const DRIFT_PX_S = 28
function useWallScroll(ref: React.RefObject<HTMLDivElement | null>, resetKey: string) {
  useEffect(() => {
    const el = ref.current
    if (!el || typeof requestAnimationFrame !== 'function') return
    const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduce) return
    let phase: 'top' | 'down' | 'bottom' | 'up' = 'top'
    let since = performance.now()
    let last = since
    let pos = 0
    el.scrollTop = 0
    let raf = 0
    const tick = (now: number) => {
      const dt = Math.min(0.1, (now - last) / 1000)
      last = now
      const max = el.scrollHeight - el.clientHeight
      if (max <= 1) {
        phase = 'top'
        since = now
        pos = 0
      } else if (phase === 'top' && now - since >= 5000) phase = 'down'
      else if (phase === 'down') {
        pos = Math.min(max, pos + DRIFT_PX_S * dt)
        el.scrollTop = pos
        if (pos >= max) (phase = 'bottom'), (since = now)
      } else if (phase === 'bottom' && now - since >= 2000) phase = 'up'
      else if (phase === 'up') {
        pos = Math.max(0, pos - 2 * DRIFT_PX_S * dt)
        el.scrollTop = pos
        if (pos <= 0) (phase = 'top'), (since = now)
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [ref, resetKey])
}
